// Time-stretch helpers (DRY/SOLID): centralize playbackRate and preservesPitch handling

export interface HasRole { role: string }

export function clampPlaybackRate(rate: number, min = 0.5, max = 2.0): number {
  return Math.max(min, Math.min(max, rate));
}

export function setPlaybackRateForAll<T extends HasRole>(
  elements: Record<string, HTMLAudioElement | null>,
  tracks: T[],
  rate: number
) {
  const r = clampPlaybackRate(rate);
  for (const t of tracks) {
    const el = elements[t.role];
    if (!el) continue;
    try { el.playbackRate = r; } catch {}
  }
}

export function setPreservesPitchForAll<T extends HasRole>(
  elements: Record<string, HTMLAudioElement | null>,
  tracks: T[],
  preservesPitch = true
) {
  for (const t of tracks) {
    const el = elements[t.role] as any;
    if (!el) continue;
    try {
      if ('preservesPitch' in el) el.preservesPitch = preservesPitch;
      if ('mozPreservesPitch' in el) el.mozPreservesPitch = preservesPitch;
      if ('webkitPreservesPitch' in el) el.webkitPreservesPitch = preservesPitch;
    } catch {}
  }
}

// Compute drift-corrected playbackRate factor for a follower track
export function computeDriftAdjustedRate(
  baseRate: number,
  errSeconds: number,
  opts?: { maxAdjust?: number; kP?: number }
): number {
  const maxAdjust = opts?.maxAdjust ?? 0.03; // +/- 3%
  const kP = opts?.kP ?? 0.6;
  const corr = 1 - Math.max(-maxAdjust, Math.min(maxAdjust, errSeconds * kP));
  return clampPlaybackRate(baseRate * corr);
}
